import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { useGetFinancialFromOneReport } from '@Domains/FinancialStatements';
import { Box, Divider, Typography } from '@mui/material';
import { formatToShortThaiDateTime, getBuddhistYear } from '@Utils/datetime';
import { CompleteSectionFormValues } from '@Utils/Types/FormValuesTypes';
import {
  validateEnglishDescription,
  validateThaiDescription,
} from '@Utils/validation';
import { useFormContext, useWatch } from 'react-hook-form';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { resetCompletedSection } from '@Utils/updateCompletedSection';

const FinancialStatementsCheckbox = () => {
  const { user } = useProfile();
  const { progressData } = useProgressBar();
  const { isPublished } = useProgressBar();
  const otherDescriptions = useWatch({ name: 'otherDescriptions' });
  const { setValue } = useFormContext();

  const companyId = user?.I_COMPANY_ID?.toString() || '';

  const startOperatingYear =
    progressData?.report?.startOperatingYear ?? getBuddhistYear();

  const [{ data: oneReportData, loading: loadingOneReportData }] =
    useGetFinancialFromOneReport(companyId, startOperatingYear, 'th');
  const hasOneReportDataFile = Boolean(oneReportData?.url);

  const completedSection =
    (useWatch({
      name: 'completedSection',
    }) as CompleteSectionFormValues) || {};

  const {
    isCompletedTh,
    isCompletedEn,
    dateTh,
    dateEn,
    roleStampTh,
    roleStampEn,
  } = completedSection;

  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';
  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  const isOtherDescriptionValidTh =
    otherDescriptions?.length >= 0
      ? validateThaiDescription(otherDescriptions)
      : true;

  const isOtherDescriptionValidEn =
    otherDescriptions?.length >= 0
      ? validateEnglishDescription(otherDescriptions)
      : true;

  const shouldDisableByMissingFile =
    !loadingOneReportData && !hasOneReportDataFile;

  const isCheckboxDisabledTh =
    loadingOneReportData ||
    isPublished ||
    !isOtherDescriptionValidTh ||
    shouldDisableByMissingFile;

  const isCheckboxDisabledEn =
    loadingOneReportData ||
    isPublished ||
    !isOtherDescriptionValidEn ||
    shouldDisableByMissingFile;

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(updatedSection, setValue);
  };

  return (
    <Box bgcolor={'common.white'}>
      <Typography fontWeight={800} p={2}>
        กรณีต้องการยืนยันความถูกต้อง
      </Typography>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight={800} textAlign={'right'}>
            ส่วนการยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{
                disabled: isCheckboxDisabledTh,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>

          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{
                disabled: isCheckboxDisabledEn,
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default FinancialStatementsCheckbox;
